import React from 'react';
import { ScrollText, Download, Filter, Search } from 'lucide-react';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { JargonTip } from '@/components/shared/JargonTip';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { formatDateTime } from '@/lib/format';
import { usePageTitle } from '@/lib/usePageTitle';

export default function AuditLogs() {
  usePageTitle('Audit Logs');
  const { events } = useApp();

  return (
    <div className="space-y-5 animate-fade-in">
      <SectionHeader
        eyebrow="Audit"
        title="System audit & access trail"
        subtitle="Every important action — contact attempts, payments, escalations, integration calls, role changes — is logged with correlation IDs and PDPL-safe payload previews."
        actions={(
          <>
            <Button variant="outline" size="sm"><Filter className="h-4 w-4 mr-1.5" />Filter</Button>
            <Button size="sm" className="bg-secondary text-secondary-foreground hover:bg-secondary/90"><Download className="h-4 w-4 mr-1.5" />Export</Button>
          </>
        )}
      />

      <div className="surface-card p-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground ml-2" aria-hidden="true" />
        <label htmlFor="audit-search" className="sr-only">Filter audit events</label>
        <Input
          id="audit-search"
          aria-label="Filter audit events by ID, case, correlation, type or source"
          placeholder="Filter by event ID, case, correlation ID, type, source…"
          className="border-0 shadow-none focus-visible:ring-0"
        />
      </div>

      <div className="surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
              <tr>
                <th scope="col" className="text-left px-5 py-2.5 font-medium">Timestamp</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Event ID</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Type</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Source / Dest</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Payload</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">
                  <span className="inline-flex items-center">Correlation<JargonTip>Trace ID that connects every event of a single transaction across services (e.g. one call ↔ its payment ↔ its audit entry).</JargonTip></span>
                </th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="font-mono-num">
              {events.map((e) => (
                <tr key={e.eventId} className="border-t border-border hover:bg-muted/40">
                  <td className="px-5 py-2 text-xs text-muted-foreground">{formatDateTime(e.timestamp)}</td>
                  <td className="px-3 py-2 text-xs">{e.eventId}</td>
                  <td className="px-3 py-2 text-xs">{e.type}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{e.src} → {e.dst}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-md">{e.payload}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{e.correlationId}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`text-[10px] ${e.status === 'success' ? 'bg-success/10 text-success border-success/40' : e.status === 'failed' ? 'bg-destructive/10 text-destructive border-destructive/30' : 'bg-warning/10 text-warning border-warning/40'}`}>{e.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
